import Toast from './toast';
import ErrorHandler from '@/common/errors';

/**
 * Toast manager markup
 * Create an Element that holds one or more toast
 * @returns {Node}
 */
function createToastsHolderElement() {
    const template = `<div class="toast-holder"></div>`;
    const wrapper = document.createElement('DIV');
    wrapper.innerHTML = template;
    return wrapper.firstChild;
}
/**
 * Class to register and manage all toast
 */
export default class ToastManager {
    toastsHolderElement = null;

    publishedToasts = [];

    /**
     * Register the toast in the manager and append it to the DOM
     * @param {Toast} toast Toast object to be appended
     * @param {boolean} stack Flag to stack or no the toast
     */
    publishToast(toast, stack = true) {
        const holderElement = this.toastsHolderElement || this.createToastsHolder();
        if (!stack) {
            this.closeAll();
        }
        holderElement.appendChild(toast.element);
        this.publishedToasts.push(toast);
    }

    /**
     * Create a new toast and display it
     * @param {string} type Valid options such as positive, warning, etc
     * @param {string} message The message to be display
     * @param {string} [title = ''] The title of the toast
     * @param {number} [delay = 50000] Total milliseconds the toast will appear
     * @param {boolean} [close = false] Flag to show close icon or no the toast
     * @param {boolean} [stack = true] Flag to stack or no the toast
     * @returns {Toast}
     */
    newToast(type, message, {
        title = '', delay = Toast.DefaultHideDelay, stack = true, close = false,
    }) {
        const toast = new Toast(type, message, title, close, this);
        toast.setupAutoHide(delay);
        toast.bindUIEvents();
        this.publishToast(toast, stack);
        toast.show();
        return toast;
    }

    /**
     * Creates and append to the Dom the toast holder
     * @returns {Node}
     */
    createToastsHolder() {
        this.toastsHolderElement = createToastsHolderElement();
        document.body.appendChild(this.toastsHolderElement);
        return this.toastsHolderElement;
    }

    /**
     * Close all register toast
     */
    closeAll() {
        this.publishedToasts.forEach((toast) => {
            toast.hide(true);
        });
    }

    /**
     * Event listener for hiding toast
     * @param {Toast} toast
     * @param {boolean} immediate Flag to postpone or no the action.
     */
    onToastHide(toast, immediate) {
        this.destroyToast(toast, immediate);
    }

    /**
     * Remove a toast from the DOM and remove publication in the manager.
     * @param {Toast} toast
     * @param {boolean} immediate Flag to postpone or no the action.
     */
    destroyToast(toast, immediate) {
        const destroyAction = () => {
            this.toastsHolderElement.removeChild(toast.element);
            this.publishedToasts = this.publishedToasts.filter(objToast => objToast !== toast);
        };
        if (immediate) {
            destroyAction();
        } else {
            setTimeout(() => destroyAction(), 500);
        }
    }

    /**
     * Create a positive toast message
     * @param {string} message Message to be display
     * @param {Object<{title: string, delay: number, stack: boolean}>} [params] Toast params
     * @returns {Toast}
     */
    positive(message, params = {}) {
        return this.newToast('positive', message, params);
    }

    /**
     * Create an informative toast message
     * @param {string} message Message to be display
     * @param {Object<{title: string, delay: number, stack: boolean}>} [params] Toast params
     * @returns {Toast}
     */
    info(message, params = {}) {
        return this.newToast('info', message, params);
    }

    /**
     * Create a warning toast message
     * @param {string} message Message to be display
     * @param {Object<{title: string, delay: number, stack: boolean}>} [params] Toast params
     * @returns {Toast}
     */
    warning(message, params = {}) {
        return this.newToast('warning', message, params);
    }

    /**
     * Create an negative toast message
     * @param {string} message Message to be display
     * @param {Object<{title: string, delay: number, stack: boolean}>} [params] Toast params
     * @returns {Toast}
     */
    negative(message, params = {}) {
        return this.newToast('negative', message, params);
    }

    fromError(error, defaultMsg, params = {}) {
        const errors = ErrorHandler.parseError(error, defaultMsg);
        errors.forEach(err => this.negative(err, params));
    }
}
